import mixpanel from "mixpanel-browser";

/*
 * time_to_first_question — how long a brand-new account takes to reach the
 * thing it came for.
 *
 * "Signup → first question" is the single most important stretch in the
 * product, and today nothing measures it. There is a signup event and there are
 * exam events; there is no way to ask how long the gap was, or whether a change
 * to onboarding shortened it.
 *
 * FAIL CLOSED, in the direction that costs a metric rather than the direction
 * that invents one. Three separate refusals, each because the alternative is a
 * plausible number that is wrong:
 *
 *   1. NO STAMP, NO EVENT. Every existing account has no start stamp, and
 *      inferring one — from a first sign-in, from a first quiz — would emit a
 *      "time to first question" of seconds for students who signed up months
 *      ago. A metric whose early data is mostly fabricated is worse than one
 *      that starts empty, because the fabrication looks like a great result.
 *
 *   2. ONE EVENT PER ACCOUNT PER STORAGE LIFETIME. Latched in localStorage,
 *      keyed by account. NOT "once per account, ever" — an earlier version of
 *      this line said that and it is false in this app: menu.jsx's sign-out
 *      calls localStorage.clear(), which destroys the latch, and no
 *      client-side latch can survive that. The duplicates are made harmless
 *      instead of impossible, by putting account_id on the event so the
 *      analysis can take the MIN per account. See the payload below.
 *
 *   3. IMPLAUSIBLE ELAPSED TIMES ARE DROPPED, NOT CLAMPED. The clock is the
 *      device's: a user crossing a timezone, correcting their clock, or
 *      restoring a backup produces negative or absurd values. Clamping would
 *      fold those into the distribution as real 0s or real 30-day tails;
 *      dropping them costs a row and keeps the median honest.
 */

export const TIME_TO_FIRST_QUESTION_EVENT = "time_to_first_question";

/*
 * Keyed BY ACCOUNT, not a global. An unkeyed marker meant a second student
 * signing up on a shared browser inherited the first one's state — and the
 * elapsed value that produced sat comfortably inside the plausible band, i.e. a
 * fabricated number that looks real. This is the same fix experiments.js and
 * referral.js already made for their own markers.
 */
const sentKeyFor = (accountId) => `qlearn_ttfq_sent:${accountId}`;

// A month. Past this the number says more about a stale localStorage entry than
// about onboarding, and a 40-day tail would drag any mean it appears in.
const MAX_PLAUSIBLE_MS = 30 * 24 * 60 * 60 * 1000;

const read = (key) => {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
  } catch (e) {
    return null; // Safari private mode throws on localStorage access.
  }
};

const write = (key, value) => {
  try {
    if (typeof window !== "undefined") window.localStorage.setItem(key, value);
  } catch (e) {
    /* storage unavailable — the metric is lost, the product is not */
  }
};

/*
 * Pure: given a start stamp and a now, what should be reported?
 * Returns { seconds } or null. Separated from the I/O so the decision — which
 * is where a wrong answer would be expensive — can be tested directly.
 */
export const resolveElapsed = (startedAtRaw, nowMs) => {
  const startedAt = Number(startedAtRaw);
  if (!Number.isFinite(startedAt) || startedAt <= 0) return null;
  if (!Number.isFinite(nowMs)) return null;
  const deltaMs = nowMs - startedAt;
  // Strictly negative is a clock that moved backwards. Zero is legitimate: a
  // fast signup on a coarse clock, and dropping it would bias the floor upward.
  if (deltaMs < 0) return null;
  if (deltaMs > MAX_PLAUSIBLE_MS) return null;
  return { seconds: Math.round(deltaMs / 1000) };
};

/*
 * Call when the student reaches their first question.
 *
 * THE START TIME COMES FROM THE SERVER. `createdAt` on /user/me is the account's
 * own creation timestamp, and the client-side stamp this replaced was deleted
 * rather than fixed, because no client-side version of it could be honest:
 *
 *   - its only call site sat on /register, a route that has been a redirect
 *     since 2026-07-11, so it emitted ZERO events and failed closed so quietly
 *     it read as "no data yet";
 *   - Google OAuth is the default sign-in tab and matches-on-email-or-creates,
 *     so the browser receives only a JWT and cannot tell a new account from a
 *     returning one — any browser stamp covers a minority of signups while
 *     reading as universal;
 *   - localStorage is per-device, so a student who signs up on web and answers
 *     first on iOS reported nothing;
 *   - `localStorage.clear()` on logout dropped everyone who signed up, left,
 *     and came back — biasing the median DOWN, toward students who did
 *     everything in one session.
 *
 * A server timestamp dissolves all four at once.
 *
 * THE WIRE CONTRACT, and both halves are load-bearing:
 *
 *   - `createdAt` is an ISO instant WITH an explicit offset ("...Z"). The
 *     server sends `Instant`, not the bare `LocalDateTime` it stores, precisely
 *     because an offsetless "2026-01-02T03:04:05.123" is parsed by a browser as
 *     LOCAL time per ES-262 — which would hand every US student a duration
 *     about five hours NEGATIVE. Do not "simplify" the server field back.
 *   - `createdAt` may legitimately be ABSENT. The server nulls it rather than
 *     let a document with no stored creation time deserialize into "created
 *     just now" (see UserCreateDtFidelityCallback). Absent means "this account
 *     has no verifiable signup time" and the correct response is to emit
 *     nothing. Never substitute a fallback here: the substitute would be a
 *     small, entirely plausible duration that no sanity check can reject, and
 *     it would drag the activation median toward zero.
 *
 * A 30-DAY WARM-UP, and it is a property of the metric rather than a bug to fix.
 *
 * The plausibility ceiling (MAX_PLAUSIBLE_MS) means an account older than 30
 * days emits nothing at all, so long-dormant students cannot report multi-year
 * "activations". But an account created in the 30 days BEFORE this ships will
 * report the gap from its signup to its first question after the ship — which
 * is not necessarily its first question. For that one window the numbers carry
 * a decaying tail of students who activated long ago.
 *
 * It is not fixable here. Knowing whether this is genuinely a student's first
 * question needs server-side history; per-device localStorage is precisely what
 * made the previous attempt dishonest. So it is stated rather than papered
 * over: the metric is trustworthy for accounts created after ship, and the
 * first 30 days of data should be read as warm-up, not as a baseline.
 *
 * Never throws: it sits on the exam page's render path, and an analytics
 * failure must not take a question down with it.
 */
export const trackFirstQuestionReached = ({ createdAt, accountId } = {}, nowMs = Date.now()) => {
  try {
    if (!createdAt || !accountId) return false; // nothing to measure against
    const sentKey = sentKeyFor(accountId);
    if (read(sentKey)) return false;

    const startedMs = Date.parse(createdAt);
    if (!Number.isFinite(startedMs)) return false;

    const elapsed = resolveElapsed(String(startedMs), nowMs);

    // Latch on an IMPLAUSIBLE value, so a bad clock cannot retry once per
    // question forever. But latch AFTER a successful emit otherwise — the
    // previous order latched first, so an ad-blocked Mixpanel (a normal
    // production state) locked the account out with no event, ever. The sibling
    // diagnostic module rejects that ordering explicitly and this one now
    // matches it.
    if (!elapsed) {
      write(sentKey, "1");
      return false;
    }

    mixpanel.track(TIME_TO_FIRST_QUESTION_EVENT, {
      seconds: elapsed.seconds,
      minutes: Math.round((elapsed.seconds / 60) * 10) / 10,
      /*
       * account_id EXISTS SO THE DOUBLE-COUNT IS RECOVERABLE, and it is not
       * decoration.
       *
       * The latch is a localStorage key, and menu.jsx's sign-out calls
       * window.localStorage.clear() — so signing out destroys it and the same
       * account reports again. A reviewer measured it: 300 seconds on the first
       * pass, then 1,728,300 seconds twenty days later for the same account.
       * The same happens on a second device, in a private window, or after
       * clearing cookies.
       *
       * The header used to claim "one event per account, EVER". That is false in
       * this app and it is not fixable client-side — a latch that survives
       * localStorage.clear() does not exist. What IS fixable is making the
       * duplicates identifiable: with an account on the event, the analysis
       * takes the MINIMUM per account and the extra emissions become harmless.
       * Without it — and the payload was {seconds, minutes} alone — the
       * duplicates were indistinguishable from real slow activations, and every
       * one of them inflated the median upward.
       *
       * MIN, specifically, and it is a guarantee rather than a convenience:
       * every duplicate shares the same createdAt and has a strictly LATER now,
       * so every extra emission is LARGER than the true first. MIN therefore
       * cannot under-report. Its only error is over-reporting, when the device
       * that saw the true first question never emitted at all (Mixpanel blocked
       * there, storage disabled) — which is the opposite of the failure this
       * module is built around. It cannot fabricate a flattering number.
       *
       * This is a QUERY-SIDE contract and nothing in the code enforces it: the
       * obvious action, charting the median in Mixpanel, silently produces the
       * inflated value and this comment cannot stop it. The rule belongs in the
       * growth readout where the metric is CONSUMED, and the real fix is a
       * server-side firstQuestionReportedAt that retires the convention.
       *
       * The opaque server id, never displayName or email. Mixpanel's own
       * distinct_id is NOT usable as the key here: PhoneAuthSection identifies
       * by .id while login.jsx and create-quiz-page.jsx identify by
       * displayName, so the same person can carry two.
       */
      account_id: accountId,
    });
    write(sentKey, "1");
    return true;
  } catch (e) {
    return false;
  }
};

// Test seam only.
export const __resetActivationTiming = (accountId) => {
  try {
    if (accountId) window.localStorage.removeItem(sentKeyFor(accountId));
  } catch (e) {
    /* nothing to reset */
  }
};
